import React from "react"

import {
    css,
    Grid,
    GridCell,
    Icon,
    Toggler,
} from "@bdfx/mergado-ui-kit"

import trans from "trans"

import { NumericInput } from "./fields";
import TransparentColor from "./transparent-color";
import { BRUSH_TYPE, CIRCLE_TEXT_TYPE, CURVED_TEXT_TYPE, RATING_TYPE } from "shape-entities/types/shape";
import { iconColor } from "template-edit/components/tools-panel";
import { TextAlign, TextVerticalAlign } from "shape-entities/types/textAlign";
import { LETTER_CASE_FIRST_LETTER_TO_LOWER, LETTER_CASE_FIRST_LETTER_TO_UPPER, LETTER_CASE_KEEP, LETTER_CASE_TO_LOWER, LETTER_CASE_TO_UPPER, LETTER_CASE_WORDS_FIRST_TO_UPPER, OVERFLOW_FONTSIZE, OVERFLOW_KEEP_AS_IS, OVERFLOW_LAYERSIZE, TEXT_DECORATION_LINETHROUGH, TEXT_DECORATION_NONE, TEXT_DECORATION_OVERLINE, TEXT_DECORATION_UNDERLINE, TEXT_HORIZONTAL_ALIGN_CENTER, TEXT_HORIZONTAL_ALIGN_LEFT, TEXT_HORIZONTAL_ALIGN_RIGHT, TEXT_ORIENTATION_HORIZONTAL, TEXT_ORIENTATION_VERTICAL, TEXT_VERTICAL_ALIGN_END, TEXT_VERTICAL_ALIGN_MIDDLE, TEXT_VERTICAL_ALIGN_START } from "shape-entities/render-text";
import { URL } from "app/config/api-config";
import { getShapeSize } from "./helpers";

import { Checkbox, MenuItem } from "@mui/material";
import {Select as SelectM} from "@mui/material"
import { getRatingShapeFontSize, getRatingShapeSize } from "template-edit/components/helper";

const { round } = Math

const iconSize = 20

const getAlignIcons = (textAlign: TextAlign): string => {
    switch (textAlign) {
        case TEXT_HORIZONTAL_ALIGN_CENTER:
            return 'text-align-center-radix'
        case TEXT_HORIZONTAL_ALIGN_RIGHT:
            return 'text-align-right-radix'
        case TEXT_HORIZONTAL_ALIGN_LEFT:
        default:
            return 'text-align-left-radix'
    }
}

const getVerticalAlignIcons = (textAlign: TextVerticalAlign): string => {
    switch (textAlign) {
        case TEXT_VERTICAL_ALIGN_START:
            return 'text-align-top-radix'
        case TEXT_VERTICAL_ALIGN_END:
            return 'text-align-bottom-radix'
        case TEXT_VERTICAL_ALIGN_MIDDLE:
        default:
            return 'text-align-middle-radix'
    }
}

const getTextDecorationIcons = (textDecoration: number): JSX.Element => {
    switch (textDecoration) {
        case TEXT_DECORATION_UNDERLINE:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/line-under.svg`} height={iconSize} title={trans('text_decoration_underline')} />
        case TEXT_DECORATION_OVERLINE:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/line-over.svg`} height={iconSize} title={trans('text_decoration_overline')} />
        case TEXT_DECORATION_LINETHROUGH:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/line-through.svg`} height={iconSize} title={trans('text_decoration_linethrough')} />
        case TEXT_DECORATION_NONE:
        default:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/line-none.svg`} height={iconSize} title={trans('text_decoration_none')} />
    }
}

const getTextLetterCaseIcons = (letterCase: number): JSX.Element => {
    switch (letterCase) {
        case LETTER_CASE_TO_UPPER:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/upper-case.svg`} height={iconSize} title={trans('to_upper_case')} />
        case LETTER_CASE_TO_LOWER:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/lower-case.svg`} height={iconSize} title={trans('to_lower_case')} />
        case LETTER_CASE_FIRST_LETTER_TO_UPPER:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/first-upper.svg`} height={iconSize} title={trans('first_letter_to_upper_case')} />
        case LETTER_CASE_FIRST_LETTER_TO_LOWER:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/first-lower.svg`} height={iconSize} title={trans('first_letter_to_lower_case')} />
        case LETTER_CASE_WORDS_FIRST_TO_UPPER:
            return <img className={'margin-top-small'} src={`${URL.publicPath}img/icons/first-word-upper.svg`} height={iconSize} title={trans('first_word_letter_to_upper_case')} />
        case LETTER_CASE_KEEP:
        default:
            return <Icon type="letter-case-toggle-radix" size={18} title={trans('keep')} />
    }
}

const getTextOverflowIcons = (textOverflow: number): JSX.Element => {
    switch (textOverflow) {
        case OVERFLOW_FONTSIZE:
            return <img className={'margin-top-medium'} src={`${URL.publicPath}img/icons/font-smaller.svg`} height={32} title={`${trans('text_overflow')}: ${trans('text_overflow_fontsize')}`} />
        case OVERFLOW_LAYERSIZE:
            return <img className={'margin-top-medium'} src={`${URL.publicPath}img/icons/enlarge-layer.svg`} height={32} title={`${trans('text_overflow')}: ${trans('text_overflow_layersize')}`} />
        case OVERFLOW_KEEP_AS_IS:
        default:
            return <img className={'margin-top-medium'} src={`${URL.publicPath}img/icons/keep-as-it-is.svg`} height={32} title={`${trans('text_overflow')}: ${trans('text_overflow_keep')}`} />
    }
}

const getTextOrientationIcons = (textOrientation: number): JSX.Element => {
    switch (textOrientation) {
        case TEXT_ORIENTATION_VERTICAL:
            return <img className={'margin-top-medium'} src={`${URL.publicPath}img/icons/text-orientation-vertical.svg`} height={32} title={trans('vertical_orientation')} />
        case TEXT_ORIENTATION_HORIZONTAL:
        default:
            return <img className={'margin-top-medium'} src={`${URL.publicPath}img/icons/text-orientation-horizontal.svg`} height={32} title={trans('horizontal_orientation')} />
    }
}

const getTextStrokeUnderlayerIcons = (textStrokeUnderlayer: boolean): JSX.Element => {
    return <img
        className={'margin-top-medium'}
        src={`${URL.publicPath}img/icons/text-stroke-underlayer-${textStrokeUnderlayer ? 'true' : 'false'}.svg`}
        height={32}
        title={trans(textStrokeUnderlayer ? 'text_stroke_underlayer' : 'text_stroke_inside')}
    />
}

const TextOptionsForm = ({ shape, activeShapeId, formName, change, updateShapeSizeDebounced }) => {
    const subFormName = `shapes.${activeShapeId}`
    const allowTextDecorationAndTextAlign = ![CIRCLE_TEXT_TYPE, CURVED_TEXT_TYPE, BRUSH_TYPE, RATING_TYPE].includes(shape.type)

    return (
        <React.Fragment>
            <StyledGrid cols={shape.type === RATING_TYPE ? 'repeat(2, auto)' : '90px 90px repeat(6, minmax(45px, 1fr))'} gap={'5px'}>
                <GridCell title={trans('text_color')}>
                    <TransparentColor
                        name={`${formName}.${subFormName}.text_color`}
                        value={shape['text_color'] || null}
                        changeValue={(color: string) => change(formName, `${subFormName}.text_color`, color)}
                    />
                </GridCell>

                <GridCell title={trans('text_fontsize')}>
                    <NumericInput
                        formName={formName}
                        fieldName={'text_fontsize'}
                        placeholder={'text_fontsize'}
                        id={'text_fontsize'}
                        style={{ width: 'auto' }}
                        min={1}
                        max={1000}
                        step={2}
                        units={'px'}
                        value={shape.text_fontsize}
                        changeValueMethod={(newSize: number) => {
                            const fontSize = parseInt(`${newSize}`.replace(/[^0-9]/g, ''))
                            if (fontSize) {
                                if (shape.type === RATING_TYPE) {
                                    const c = fontSize / shape.text_fontsize

                                    const newTextPadding = shape.text_padding ? round(c * shape.text_padding) : shape.text_padding
                                    const newCornerRadius = shape.corner_radius ? round(c * shape.corner_radius) : shape.corner_radius

                                    const ns = getRatingShapeSize(fontSize, newTextPadding, shape.stroke_width)

                                    const updatedShape = {
                                        ...shape,
                                        text_fontsize: fontSize,
                                        text_padding: newTextPadding,
                                        corner_radius: newCornerRadius,
                                        width: ns.width,
                                        height: ns.height,
                                    }
                                    change(formName, `${subFormName}`, updatedShape)
                                } else {
                                    change(formName, `shapes.${activeShapeId}.text_fontsize`, fontSize)
                                }

                                updateShapeSizeDebounced(subFormName, activeShapeId, true)

                            }
                            return fontSize
                        }}
                        onlyPositive={true}
                    />
                </GridCell>

                {allowTextDecorationAndTextAlign &&
                    <React.Fragment>
                        <GridCell title={trans('text_align')} className="shadow-box">
                            <StyledMSelect
                                value={shape.text_align || TEXT_HORIZONTAL_ALIGN_CENTER}
                                onChange={(event) => change(formName, `shapes.${activeShapeId}.text_align`, event.target.value)}>
                                {[TEXT_HORIZONTAL_ALIGN_LEFT, TEXT_HORIZONTAL_ALIGN_CENTER, TEXT_HORIZONTAL_ALIGN_RIGHT].map((v: TextAlign) => (<MenuItem key={`text_align_${v}`} value={v}><Icon type={getAlignIcons(v)} color={iconColor} size={iconSize} /></MenuItem>))}
                            </StyledMSelect>
                        </GridCell>

                        <GridCell title={trans('text_valign')} className="shadow-box">
                            <StyledMSelect
                                value={shape.text_valign || TEXT_VERTICAL_ALIGN_MIDDLE}
                                onChange={(event) => change(formName, `shapes.${activeShapeId}.text_valign`, event.target.value)}>
                                {[TEXT_VERTICAL_ALIGN_START, TEXT_VERTICAL_ALIGN_MIDDLE, TEXT_VERTICAL_ALIGN_END].map((v: TextVerticalAlign) => (<MenuItem key={`text_valign_${v}`} value={v}><Icon type={getVerticalAlignIcons(v)} color={iconColor} size={iconSize} /></MenuItem>))}
                            </StyledMSelect>
                        </GridCell>

                        <GridCell title={trans('text_decoration')} className="shadow-box">
                            <StyledMSelect
                                value={shape.text_decoration || TEXT_DECORATION_NONE}
                                onChange={(event) => {
                                    const textDecoration = event.target.value
                                    change(formName, `shapes.${activeShapeId}`, {
                                        ...shape,
                                        text_decoration: textDecoration,
                                        text_decoration_color: shape.text_decoration_color ? shape.text_decoration_color : shape.text_color,
                                    })
                                }}>
                                {[TEXT_DECORATION_NONE, 2, 3, 4].map((v: number) => (<MenuItem key={`text_decoration_${v}`} value={v}>{getTextDecorationIcons(v)}</MenuItem>))}
                            </StyledMSelect>
                        </GridCell>

                        <GridCell title={trans('text_overflow')} className="shadow-box">
                            <StyledMSelect
                                value={shape.text_overflow || OVERFLOW_KEEP_AS_IS}
                                onChange={(event) => change(formName, `shapes.${activeShapeId}.text_overflow`, event.target.value)}>
                                {[OVERFLOW_KEEP_AS_IS, OVERFLOW_FONTSIZE, OVERFLOW_LAYERSIZE].map((v: number) => (<MenuItem key={`text_overflow_${v}`} value={v}>{getTextOverflowIcons(v)}</MenuItem>))}
                            </StyledMSelect>
                        </GridCell>

                        <GridCell title={trans('text_orientation')} className="shadow-box">
                            <StyledMSelect
                                value={shape.text_orientation || TEXT_ORIENTATION_HORIZONTAL}
                                onChange={(event) => {
                                    const textOrientation = event.target.value
                                    const shapeSize = getShapeSize(shape)
                                    const updatedShape = {
                                        ...shape,
                                        text_orientation: textOrientation,
                                        width: shapeSize.height,
                                        height: shapeSize.width,
                                    }
                                    change(formName, subFormName, updatedShape)
                                    updateShapeSizeDebounced(subFormName, activeShapeId, true, true) //FIE-708
                                }}>
                                {[TEXT_ORIENTATION_HORIZONTAL, TEXT_ORIENTATION_VERTICAL].map((v: number) => (<MenuItem key={`text_orientation_${v}`} value={v}>{getTextOrientationIcons(v)}</MenuItem>))}
                            </StyledMSelect>
                        </GridCell>
                    </React.Fragment>
                }

                {shape.type !== RATING_TYPE &&
                    <GridCell title={trans('letter_case')} className="shadow-box">
                        <StyledMSelect
                            value={shape.letter_case || LETTER_CASE_KEEP}
                            onChange={(event) => change(formName, `${subFormName}.letter_case`, event.target.value)}>
                            {[LETTER_CASE_KEEP, 1, 2, 3, 4, 5].map((v: number) => (<MenuItem key={`letter_case_${v}`} value={v}>{getTextLetterCaseIcons(v)}</MenuItem>))}
                        </StyledMSelect>
                    </GridCell>
                }

                <GridCell title={trans('text_stroke')}>
                    <TransparentColor
                        name={`${formName}.${subFormName}.text_stroke_color`}
                        value={shape.text_stroke_color || ''}
                        changeValue={(color: string) => {
                            change(formName, `${subFormName}.text_stroke_color`, color)
                            if (color && !shape.text_stroke_width) {
                                change(formName, `${subFormName}.text_stroke_width`, 1)
                            }
                        }}
                        notRequired={true}
                        hideGradients={true}
                    />
                </GridCell>

                <GridCell>
                    <NumericInput
                        formName={formName}
                        fieldName={'text_stroke_width'}
                        placeholder={'text_stroke_width'}
                        shapeIndex={shape.index}
                        id={'stroke-input'}
                        min={0}
                        max={100}
                        step={1}
                        units={'px'}
                        value={round(shape.text_stroke_width)}
                        onlyPositive={true}
                        onChangeFunction={(newValue: any) => {
                            if (newValue && !shape.text_stroke_color) {
                                change(formName, `${subFormName}.text_stroke_color`, '#ffffff')
                            } else if (!newValue) {
                                change(formName, `${subFormName}.text_stroke_color`, null)
                            }
                        }}
                    />
                </GridCell>

                {!!(shape.text_stroke_color && shape.text_stroke_width) &&
                    <GridCell title={`${trans('text_stroke_underlayer')} vs ${trans('text_stroke_inside')}`} className="shadow-box">
                        <StyledMSelect
                            value={shape.text_stroke_underlayer ? 1 : 0}
                            onChange={(event) => {
                                const textStrokeUnderlayer = event.target.value
                                const updatedShape = {
                                    ...shape,
                                    text_stroke_underlayer: textStrokeUnderlayer,
                                }
                                change(formName, subFormName, updatedShape)
                            }}>
                            {[false, true].map((v: boolean) => (<MenuItem key={`text_stroke_underlayer_${v}`} value={v ? 1 : 0}>{getTextStrokeUnderlayerIcons(v)}</MenuItem>))}
                        </StyledMSelect>
                    </GridCell>
                }
            </StyledGrid>
        </React.Fragment>
    )
}

const StyledMSelect = css(SelectM)({
    ' .MuiSelect-select': {
        padding: '6px',
        paddingRight: '0 !important',
    },
    ' .MuiOutlinedInput-notchedOutline': {
        borderStyle: 'none',
    },
    ' .MuiSelect-icon': {
        display: 'none',
    },
})

const StyledGrid = css(Grid)({
    marginTop: '10px',
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
        borderRadius: '4px',
    },
    ' img.margin-top-small': {
        margin: '-5px 0px',
    },
    ' img.margin-top-medium': {
        margin: '-8px 2px',
    },
    ' img': {
        maxWidth: '26px',
        maxHeight: '26px',
    },
    ' .m-button-href, .m-button-void': {
        display: 'block !important',
        height: '32px',
        alignItems: 'center',
    },
    ' .muk-button-void.secondary': {
        padding: '4px 0px',
    },
})

export default TextOptionsForm